import { PropType } from 'vue';
import { mapState } from 'vuex';
import plantsClient from '@/core/services/plants';
import { Options, Vue } from 'vue-class-component';
import {
  BuildingDTO,
  EPDeviceDTO,
  EPDeviceTypeEnum,
  EntrancePlantDetailDTO,
  RiserDTO,
  SwitchboardConfigDTO,
  SwitchboardDTO,
} from '@/../client-generator/generated-clients/plants';
import { SET_APP_LOADING } from '@/core/store/action-types';
import { isNumber, isString } from '@microsoft/applicationinsights-core-js';

@Options({
  name: 'DialogAddEditSwitchboard',
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    switchboardToEdit: {
      type: Object as PropType<SwitchboardDTO>,
      required: false,
    },
  },
  computed:{
    ...mapState(['currentEntrancePlant'])
  },
  watch: {
    isVisible: function (newValue) {
      this.isDialogVisible = newValue;
      if (this.isDialogVisible) {
        this.invalidNameReason = '';
        this.isValidName = true;
        this.isValidScs = true;
        this.invalidScsReason = '';
        this.switchboardDevicesIds = [];
        this.selectedBuilding = null;
        this.buildingRisers = [];
        this.initSwitchboard();
      }
    },
    'newSwitchboard.switchboard.name': function () {
      if (this.newSwitchboard.switchboard) {
        this.verifyValidityOfName();
      }
    },
    'newSwitchboard.switchboard.scsAddr': function () {
      if (this.newSwitchboard.switchboard) {
        this.verifyValidityOfScsAddress();
      }
    },
    'selectedBuilding': function() {
      if(!this.isEdit && this.step == 0 && this.currentEntrancePlant.hasRiserInterface)
      {
        this.buildingRisers = this.fillBuildingRiserDropdown();
        this.newSwitchboard.switchboard.riserInterfaceId = this.buildingRisers[0]?.riserInterfaceId ?? null;
      }
    }
  },
  emits: ['hide-dialog', 'update-list'],
})
export default class DialogAddEditSwitchboard extends Vue {
  newSwitchboard: SwitchboardConfigDTO = new SwitchboardConfigDTO();
  currentEntrancePlant!: EntrancePlantDetailDTO;
  switchboardToEdit!: SwitchboardDTO;
  isVisible!: boolean;
  isDialogVisible = false;
  isEdit = false;
  readonly nameMaxLength = 50;
  switchboardDevicesIds: string[] = [];
  step = 0;
  // Naming validation props
  isValidName = true;
  isValidScs = true;
  invalidNameReason: string = '';
  invalidScsReason: string = '';

  // For riser interface plants
  selectedBuilding: BuildingDTO | null = null;
  buildingRisers: {name: string; riserInterfaceId: string}[] = [];

  mounted() {
    this.isDialogVisible = this.isVisible;
  }

  async initSwitchboard() {
    if (this.switchboardToEdit) {
      this.isEdit = true;
      this.step = 1;
      this.newSwitchboard.switchboard = new SwitchboardDTO(
        this.switchboardToEdit
      );
      this.newSwitchboard.devices = await this.checkIfSwitchboardHasDevices();
      if (this.currentEntrancePlant.hasRiserInterface) 
      {   
        this.selectedBuilding = this.switchboardToEdit.inBackboneInterface 
          ? this.buildings.find((building) => building.backboneInterface) ?? null
          : this.buildings.find((building) =>
              building.risers?.some((riser) =>
                riser.interfaces?.some(
                  (intf) => intf.riserInterfaceId === this.newSwitchboard.switchboard?.riserInterfaceId
                ))) ?? null;
        this.buildingRisers = this.fillBuildingRiserDropdown();
      }
    } else {
      this.isEdit = false;
      this.step = this.currentEntrancePlant.hasRiserInterface ? 0 : 1;
      this.newSwitchboard.switchboard = new SwitchboardDTO();
      this.newSwitchboard.switchboard.scsAddr = this.currentEntrancePlant.scsAddressHelper?.firstAvailableScsAddressSwitchboard;
      this.newSwitchboard.devices = [];
      this.newSwitchboard.switchboard.name = this.getSwitchboardName();
      if (this.currentEntrancePlant.hasRiserInterface) 
      {      
        this.selectedBuilding = this.buildings[0];
        this.buildingRisers = this.fillBuildingRiserDropdown();
        this.newSwitchboard.switchboard.riserInterfaceId = this.buildingRisers[0]?.riserInterfaceId ?? null;
      }
    }
  }

  handleHideDialog() {
    if (this.step == 0 || this.isEdit || !this.currentEntrancePlant.hasRiserInterface) {
      this.isDialogVisible = false;
    } else {
      this.step = 0;
    }
  }

  doHideDialog() {
    this.$emit('hide-dialog');
  }

  get dialogTitle() {
    return this.isEdit
      ? this.$t('AppResourceEP.SwitchboardEditTitle')
      : this.$t('AppResourceEP.SwitchboardAddTitle');
  }

  get buttonSubmitLabel() {
    return this.step == 0 ? this.$t('AppResource.Next') : this.$t('AppResource.Save');
  }

  get devices() {
    const deviceList = this.fillDeviceList();
    return deviceList;
  }

  get saveButtonDisabled() {
    if (this.step == 0) return false;
    const disabledForNameCheck =
      !this.isValidName || !this.newSwitchboard.switchboard?.name?.trim();
    const disabledForScsCheck = !this.isValidScs;
    return disabledForNameCheck || disabledForScsCheck;
  }

  get buildings() {
    return this.currentEntrancePlant.buildings ?? [];
  }

  fillBuildingRiserDropdown() {
    if (!this.selectedBuilding) return [];
    const result: {name: string; riserInterfaceId: string}[] = [];
    if (this.selectedBuilding.backboneInterface) {
      result.push({
        name: this.$t('AppResourceEP.Backbone') as string,
        riserInterfaceId: this.selectedBuilding.backboneInterface.riserInterfaceId ?? ''
      });
    }
    if (this.selectedBuilding.risers) {
      for (const riser of this.selectedBuilding.risers) {
        result.push({
          name: riser.name ?? '',
          riserInterfaceId: riser?.interfaces?.[0]?.riserInterfaceId ?? ''
        });
      }
    }
    return result;
  }

  fillDeviceList() {
    if (this.currentEntrancePlant.gates === undefined) return [];
    const EPDeviceList: {
      device: EPDeviceDTO;
      gate: string;
    }[] = [];

    let filteredGates = this.currentEntrancePlant.gates.filter(
      (gate) => gate.devices?.some(
        (device) => device.type === EPDeviceTypeEnum.Linea5000
      ));

    if (this.currentEntrancePlant.hasRiserInterface) {
      const selectedRiserInterfaceId = this.newSwitchboard.switchboard?.riserInterfaceId;
      filteredGates = filteredGates.filter(
        (gate) => gate.riserInterfaceId === selectedRiserInterfaceId
      );
    }
    
    for (const gate of filteredGates) {
      if (gate.devices) {
        for (const device of gate.devices) {
          EPDeviceList.push({
            device: device,
            gate: gate.name ?? '',
          });
        }
      }
    }
    return EPDeviceList;
  }

  async checkIfSwitchboardHasDevices() {
    if (this.currentEntrancePlant.gates === undefined) return [];
    const gates = this.currentEntrancePlant.gates;
    let selectedDevices: EPDeviceDTO[] = [];
    for (const gate of gates) {
      if (gate.devices) {
        for (const device of gate.devices) {
          if (
            device.switchboardId ===
            this.newSwitchboard.switchboard?.switchboardId
          ) {
            selectedDevices.push(device);
            this.switchboardDevicesIds.push(device.deviceId!);
          }
        }
      }
    }
    return selectedDevices;
  }

  getSwitchboardName() {
    let baseName = `${this.$t('AppResourceEP.Switchboard')}`
    let name ='';
    let i = 0;
    do {
      i++;
      name = `${baseName} ${(this.currentEntrancePlant.switchboards?.length ?? 0) + i}`;
    } while (this.alreadyExist(name));

    return name;
  }

  alreadyExist(value: string | number): boolean {
    let listToCheck = this.currentEntrancePlant.switchboards || [];
    if (this.isEdit) {
      listToCheck = listToCheck.filter(
        (switchb) => switchb.switchboardId !== this.switchboardToEdit.switchboardId
      );
    }
    if (isString(value)) {
      const nameExist = listToCheck.some(
        (existing) => existing.name?.toLowerCase().trim() === (value as string).toLowerCase().trim()
      );
      return nameExist;
    } else if(isNumber(value)) {
      const addrExist = listToCheck.some(
        (existing) => existing.scsAddr === value as number 
      );
      return addrExist;
    }
    return true
  }

  verifyValidityOfName() {
    if (this.newSwitchboard.switchboard?.name === undefined) return;

    this.newSwitchboard.switchboard.name = this.newSwitchboard.switchboard.name.trim();
    this.isValidName = !this.alreadyExist(this.newSwitchboard.switchboard.name);
    this.invalidNameReason = this.isValidName
      ? ''
      : this.$t('general.alreadyExisting');
  }

  verifyValidityOfScsAddress() {
    if (this.newSwitchboard.switchboard?.scsAddr === undefined) return false;
    
    const value = Number(this.newSwitchboard.switchboard.scsAddr);

    if (this.isEdit && this.switchboardToEdit.scsAddr === value) {
      this.isValidScs = true;
      this.invalidScsReason = '';
      return;
    }
    const acceptedRange = value >= 0 && value <= 15;
    const isItAvailable = this.currentEntrancePlant.scsAddressHelper?.availableScsAddressSwitchboard?.includes(value) ?? false;
    const doesNotExist = !this.alreadyExist(value)
    this.isValidScs = acceptedRange && doesNotExist && isItAvailable;
    if (!acceptedRange) {
      this.invalidScsReason = this.$t('AppResourceEP.SwitchboardScsAddressOutOfRange');
    } else if (!doesNotExist) {
      this.invalidScsReason = this.$t('AppResourceEP.ScsAddressAlreadyInUse');
    } else if (!isItAvailable) {
      this.invalidScsReason = this.$t('AppResourceEP.InputNotValid');
    } else {
      this.invalidScsReason = '';
    }
  }

  async handleButtonSubmitAction() {
    if (this.step == 0) {
      this.step = 1;
      return;
    }

    this.isDialogVisible = false;

    await this.saveSwitchboard();

    this.$emit('update-list');   
  }

  async saveSwitchboard() {
    if (this.currentEntrancePlant.desktopId === undefined) return;
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      this.newSwitchboard.devices = this.devices
        .filter((device) => this.switchboardDevicesIds.includes(device.device.deviceId!))
        .map((device) => device.device);

      if (this.isEdit) {
        await plantsClient.switchboardPUT(
          this.currentEntrancePlant.desktopId,
          new SwitchboardConfigDTO(this.newSwitchboard)
        );
      } else {
        await plantsClient.switchboardPOST(
          this.currentEntrancePlant.desktopId,
          this.newSwitchboard
        );
      }

      this.$toast.add({
        severity: 'success',
        summary: this.isEdit
          ? this.$t('entrancePlant.editedSwitchboard')
          : this.$t('AppResourceEP.SwitchboardBannerCreated'),
        closable: false,
        life: 3000,
      });

    } catch (error) {
      console.log(error);
      setTimeout(() => {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('general.operationError'),
          closable: false,
          life: 3000,
        });
      }, 1000);
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  getRiserName(switchboard?: SwitchboardDTO): string {
    if (!switchboard || !switchboard.riserInterfaceId) return '';

    const riserInterfaceId = switchboard.riserInterfaceId;
    if (riserInterfaceId === this.selectedBuilding?.backboneInterface?.riserInterfaceId) {
      return this.$t('AppResourceEP.Backbone') as string;
    }
    const riser = this.selectedBuilding?.risers?.find(riser => 
      riser.interfaces?.some(intf => intf.riserInterfaceId === riserInterfaceId)
    );
    return riser?.name ?? '';
  }

  getBuildingName(switchboard?: SwitchboardDTO): string {
    if (!switchboard) return '';
    return this.selectedBuilding?.name ?? '';
  }
}
